import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getSpecies } from '../services/speciesService';
import SpeciesEditorModal from '../components/harvesting/SpeciesEditorModal';
import useSpeciesAccess from '../hooks/useSpeciesAccess';

export default function SpeciesProfilePage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [species, setSpecies] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState('');
  const [editing, setEditing] = useState(false);
  const { canEdit, canArchive } = useSpeciesAccess();

  useEffect(() => {
    let current = true;
    setLoading(true);
    setError('');
    getSpecies(id)
      .then((r) => { if (current) setSpecies(r.data); })
      .catch((err) => { if (current) setError(err.response?.data?.message || 'Could not load species profile.'); })
      .finally(() => { if (current) setLoading(false); });
    return () => { current = false; };
  }, [id]);

  if (loading) return <div style={{ padding: 32 }}>Loading…</div>;
  if (error)   return <div style={{ padding: 32, color: '#b91c1c' }}>{error}</div>;
  if (!species) return null;

  const LINK_LABELS = {
    ontario:           'Ontario Species at Risk',
    ontarioFishOnline: 'Ontario Fish ON-Line',
    canadaSara:        'Canada SARA Registry',
    cosewic:           'COSEWIC Assessment',
    inaturalist:       'iNaturalist',
    gbif:              'GBIF',
    wikimedia:         'Wikimedia Commons',
  };

  const links = Object.entries(LINK_LABELS)
    .filter(([k]) => species.externalLinks?.[k])
    .map(([k, label]) => ({ label, url: species.externalLinks[k] }));
  if (species.externalLinks?.other) links.push(...(species.externalLinks.other || []));

  const currentStatuses = (species.officialStatuses || []).filter((s) => s.isCurrent);

  return (
    <div style={{ maxWidth: 680, margin: '0 auto', padding: '28px 20px' }}>
      <button
        onClick={() => navigate(-1)}
        style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#2563eb', fontSize: 14, marginBottom: 16, padding: 0 }}
      >
        ← Back
      </button>

      {species.image?.url && (
        <img
          src={species.image.url}
          alt={species.commonName}
          style={{ width: '100%', borderRadius: 10, maxHeight: 260, objectFit: 'cover', marginBottom: 20 }}
        />
      )}
      {!species.image?.url && (
        <section aria-label="Species photos" style={{ border: '1px dashed #cbd5e1', borderRadius: 10, padding: 20, marginBottom: 20, background: '#f8fafc' }}>
          <h2 style={{ fontSize: 16, margin: '0 0 8px' }}>Species photos</h2>
          <p style={{ margin: 0 }}>No photo added yet. This space is reserved for future species images, with photographer, source and licence details.</p>
        </section>
      )}
      {species.image?.url && (species.image.photographer || species.image.attribution || species.image.license || species.image.sourceUrl) && (
        <p style={{ fontSize: 12, color: '#64748b' }}>
          {[species.image.photographer, species.image.attribution, species.image.license].filter(Boolean).join(' | ')}
          {species.image.sourceUrl && <> <a href={species.image.sourceUrl} target="_blank" rel="noreferrer">Image source</a></>}
        </p>
      )}

      <h1 style={{ margin: '0 0 4px', fontSize: 26, color: '#1e293b' }}>{species.commonName}</h1>
      {canEdit && <button type="button" onClick={() => setEditing(true)}>Edit species</button>}
      {species.isActive === false && <p>Archived - unavailable for new selections. Existing records and guidance are preserved.</p>}
      {species.communityName && (
        <p style={{ margin: '0 0 4px', fontSize: 15, color: '#16a34a', fontStyle: 'italic' }}>{species.communityName}</p>
      )}
      {species.alternateNames?.length > 0 && (
        <p style={{ margin: '0 0 8px', fontSize: 13, color: '#64748b' }}>
          Also known as: {species.alternateNames.join(' · ')}
        </p>
      )}
      {species.scientificName && (
        <p style={{ margin: '0 0 16px', fontSize: 14, color: '#475569', fontStyle: 'italic' }}>{species.scientificName}</p>
      )}

      <div style={{ display: 'inline-block', background: '#f1f5f9', borderRadius: 6, padding: '3px 10px', fontSize: 12, color: '#475569', marginBottom: 20 }}>
        {species.category}
      </div>
      {species.harvestCategory && <p>Default harvest category: {species.harvestCategory.replace(/_/g, ' ')}</p>}
      {species.subType && <p>Sub-type: {species.subType}</p>}
      {![species.shortDescription, species.habitat, species.seasonalNotes, species.stewardshipNote].some(Boolean) &&
        <p>Profile notes have not been added yet.{canEdit ? ' Use Edit species to add a description, habitat, seasonal notes and stewardship information.' : ''}</p>}

      {species.shortDescription && (
        <div style={{ marginBottom: 18 }}>
          <h3 style={{ fontSize: 14, fontWeight: 700, color: '#334155', margin: '0 0 6px' }}>About</h3>
          <p style={{ fontSize: 14, color: '#1e293b', lineHeight: 1.7 }}>{species.shortDescription}</p>
        </div>
      )}
      {species.habitat && (
        <div style={{ marginBottom: 18 }}>
          <h3 style={{ fontSize: 14, fontWeight: 700, color: '#334155', margin: '0 0 6px' }}>Habitat</h3>
          <p style={{ fontSize: 14, color: '#1e293b', lineHeight: 1.7 }}>{species.habitat}</p>
        </div>
      )}
      {species.seasonalNotes && (
        <div style={{ marginBottom: 18 }}>
          <h3 style={{ fontSize: 14, fontWeight: 700, color: '#334155', margin: '0 0 6px' }}>Seasonal notes</h3>
          <p style={{ fontSize: 14, color: '#1e293b', lineHeight: 1.7 }}>{species.seasonalNotes}</p>
        </div>
      )}

      {species.stewardshipNote && (
        <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8, padding: '14px 16px', marginBottom: 18 }}>
          <h3 style={{ fontSize: 14, fontWeight: 700, color: '#166534', margin: '0 0 6px' }}>RSMIN Stewardship Note</h3>
          <p style={{ fontSize: 14, color: '#166534', lineHeight: 1.7, margin: 0 }}>{species.stewardshipNote}</p>
        </div>
      )}

      {currentStatuses.length > 0 && (
        <div style={{ marginBottom: 18 }}>
          <h3 style={{ fontSize: 14, fontWeight: 700, color: '#334155', margin: '0 0 8px' }}>Official Conservation Status</h3>
          {currentStatuses.map((st, i) => (
            <div key={i} style={{ fontSize: 13, marginBottom: 6, padding: '8px 12px', background: '#fafafa', borderRadius: 6, border: '1px solid #e2e8f0' }}>
              <span style={{ fontWeight: 600, color: '#1e293b' }}>{st.jurisdiction}</span>
              {' — '}
              <span>{st.status}</span>
              {st.retrievedAt && <span style={{ color: '#94a3b8', marginLeft: 8 }}>Last verified: {new Date(st.retrievedAt).toLocaleDateString()}</span>}
              {st.sourceUrl && <a href={st.sourceUrl} target="_blank" rel="noreferrer" style={{ color: '#2563eb', marginLeft: 8, fontSize: 12 }}>Source ↗</a>}
            </div>
          ))}
          <p style={{ fontSize: 11, color: '#94a3b8', marginTop: 4 }}>
            Stewardship guidance reflects local RSMIN knowledge and does not by itself determine legal harvesting permissions or rights.
          </p>
        </div>
      )}

      {links.length > 0 && (
        <div style={{ marginBottom: 24 }}>
          <h3 style={{ fontSize: 14, fontWeight: 700, color: '#334155', margin: '0 0 8px' }}>Learn More</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {links.map((l, i) => (
              <a key={i} href={l.url} target="_blank" rel="noreferrer" style={{ color: '#2563eb', fontSize: 14 }}>
                {l.label} ↗
              </a>
            ))}
          </div>
        </div>
      )}
      {editing && canEdit && <SpeciesEditorModal
        species={species}
        canArchive={canArchive}
        archived={species.isActive === false}
        onClose={() => setEditing(false)}
        onSaved={(saved) => { setSpecies(saved); setEditing(false); }}
      />}
    </div>
  );
}
